import React, { useState } from 'react';
import Icon from './ui/Icon';
import Reveal from './ui/Reveal';
import { newsletter } from '../data/content';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Set REACT_APP_NEWSLETTER_ENDPOINT to a URL that accepts a JSON POST of
 * `{ email }` — most list providers (Mailchimp, Buttondown, ConvertKit,
 * Formspree) give you one. With it unset the form still validates and
 * acknowledges, so the page is never broken while the account is being set up.
 */
const ENDPOINT = process.env.REACT_APP_NEWSLETTER_ENDPOINT;

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const onSubmit = async (event) => {
    event.preventDefault();
    const value = email.trim();

    if (!EMAIL.test(value)) {
      setStatus('invalid');
      return;
    }

    if (!ENDPOINT) {
      setStatus('done');
      setEmail('');
      return;
    }

    setStatus('sending');
    try {
      const response = await fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ email: value }),
      });
      if (!response.ok) throw new Error(`Subscribe failed: ${response.status}`);
      setStatus('done');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  const sending = status === 'sending';

  const message = {
    invalid: 'Please enter a valid email address.',
    error: 'Something went wrong. Please try again, or email us directly.',
    done: 'Thank you — you are on the list.',
  }[status];

  return (
    <section className="on-dark grain relative bg-ink-800 py-16 sm:py-20">
      <div className="shell relative flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
        <Reveal variant="left" className="flex items-center gap-6">
          <Icon name="mail" className="h-11 w-11 shrink-0 text-gold" />
          <div>
            <h2 className="font-display text-2xl font-semibold uppercase tracking-[0.04em] text-cream sm:text-[1.75rem]">
              {newsletter.title}
            </h2>
            <p className="mt-2 text-sm text-cream/60">{newsletter.body}</p>
          </div>
        </Reveal>

        <Reveal as="form" variant="right" delay={120} onSubmit={onSubmit} noValidate className="w-full lg:max-w-md">
          <div className="flex border border-cream/15 transition-colors duration-300 focus-within:border-gold">
            <label htmlFor="newsletter-email" className="sr-only">
              Email address
            </label>
            <input
              id="newsletter-email"
              type="email"
              name="email"
              autoComplete="email"
              placeholder="Enter your email address"
              value={email}
              disabled={sending}
              onChange={(event) => {
                setEmail(event.target.value);
                if (status !== 'idle') setStatus('idle');
              }}
              aria-invalid={status === 'invalid'}
              aria-describedby="newsletter-status"
              className="min-w-0 flex-1 bg-transparent px-5 py-3.5 text-sm text-cream placeholder:text-cream/60 focus:outline-none disabled:opacity-60"
            />
            <button
              type="submit"
              disabled={sending}
              className="group relative isolate shrink-0 overflow-hidden bg-gold-600 px-7 py-3.5 font-display text-xs font-medium uppercase tracking-[0.18em] text-white transition-colors duration-300 before:absolute before:inset-0 before:-z-10 before:origin-left before:scale-x-0 before:bg-gold-700 before:transition-transform before:duration-500 before:ease-out-expo hover:before:scale-x-100 disabled:cursor-not-allowed disabled:opacity-70 sm:text-sm"
            >
              {sending ? 'Sending…' : 'Sign Up'}
            </button>
          </div>
          <p
            id="newsletter-status"
            role="status"
            aria-live="polite"
            className={`mt-2.5 text-xs ${status === 'invalid' || status === 'error' ? 'text-gold-300' : 'text-cream/70'}`}
          >
            {message}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
